import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Grid,
    FormControlLabel,
    Checkbox,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    IconButton,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack, Save as SaveIcon, Add as AddIcon, Close as CloseIcon, ErrorOutline, CameraAlt } from '@mui/icons-material';
import { memberService } from '../services/memberService';
import MemberPictureCamera from '../components/MemberPictureCamera';

// Default grey-scale empty head avatar when no picture
const DEFAULT_AVATAR = "data:image/svg+xml," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120">' +
    '<ellipse cx="50" cy="42" rx="36" ry="38" fill="#9e9e9e"/>' +
    '<path d="M18 82 Q50 100 82 82 L82 120 L18 120 Z" fill="#bdbdbd"/>' +
    '</svg>'
);

const MemberEdit = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const member = location.state?.member;

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        gender: '',
        dateOfBirth: '',
        phone: '',
        email: '',
        address1: '',
        address2: '',
        city: '',
        province: '',
        country: '',
        postalCode: '',
        familySize: '',
        allergy: '',
        notificationConsent: false,
        notes: '',
        isActive: true,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [allergies, setAllergies] = useState([]);
    const [selectedAllergies, setSelectedAllergies] = useState([]);
    const [addAllergyDialogOpen, setAddAllergyDialogOpen] = useState(false);
    const [otherAllergyText, setOtherAllergyText] = useState('');
    const [pictureUrl, setPictureUrl] = useState(null);
    const [capturedPicture, setCapturedPicture] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const pictureUrlRef = useRef(null);

    useEffect(() => {
        if (!member) {
            setError('No member data provided');
            return;
        }

        // Pre-populate form with member data
        setFormData({
            firstName: member.FirstName || '',
            lastName: member.LastName || '',
            gender: member.Gender || '',
            dateOfBirth: member.DateOfBirth ? member.DateOfBirth.split('T')[0] : '',
            phone: member.Phone || '',
            email: member.Email || '',
            address1: member.Address1 || '',
            address2: member.Address2 || '',
            city: member.City || '',
            province: member.Province || '',
            country: member.Country || '',
            postalCode: member.PostalCode || '',
            familySize: member.FamilySize || '',
            allergy: member.Allergy || '',
            notificationConsent: member.NotificationConsent || false,
            notes: member.Notes || '',
            isActive: member.IsActive !== undefined ? member.IsActive : true,
        });

        // Parse existing allergies
        if (member.Allergy) {
            const allergyList = member.Allergy.split(',').map(a => a.trim()).filter(a => a);
            setSelectedAllergies(allergyList);
        }
    }, [member]);

    // Fetch current member picture
    useEffect(() => {
        if (!member?.ID) return;
        pictureUrlRef.current = null;
        const fetchPicture = async () => {
            try {
                const result = await memberService.getMemberPicture(member.ID);
                const url = result?.url ?? null;
                pictureUrlRef.current = url;
                setPictureUrl(url);
            } catch (err) {
                setPictureUrl(null);
            }
        };
        fetchPicture();
        return () => {
            const url = pictureUrlRef.current;
            if (url && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        };
    }, [member?.ID]);

    // Fetch available allergies
    useEffect(() => {
        const fetchAllergies = async () => {
            try {
                const response = await memberService.getAllergies();
                setAllergies(response.allergies || []);
            } catch (error) {
                console.error('Error fetching allergies:', error);
            }
        };

        fetchAllergies();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Allergy management functions
    const handleRemoveAllergy = (allergyToRemove) => {
        const updatedAllergies = selectedAllergies.filter(allergy => allergy !== allergyToRemove);
        setSelectedAllergies(updatedAllergies);
        setFormData(prev => ({
            ...prev,
            allergy: updatedAllergies.join(', ')
        }));
    };

    const handleAddAllergy = (allergyName) => {
        if (allergyName === 'Other' && otherAllergyText.trim()) {
            const updatedAllergies = [...selectedAllergies, otherAllergyText.trim()];
            setSelectedAllergies(updatedAllergies);
            setFormData(prev => ({
                ...prev,
                allergy: updatedAllergies.join(', ')
            }));
            setOtherAllergyText('');
        } else if (allergyName !== 'Other') {
            const updatedAllergies = [...selectedAllergies, allergyName];
            setSelectedAllergies(updatedAllergies);
            setFormData(prev => ({
                ...prev,
                allergy: updatedAllergies.join(', ')
            }));
        }
        setAddAllergyDialogOpen(false);
    };

    const getAvailableAllergies = () => {
        const availableFromDB = allergies.filter(allergy => !selectedAllergies.includes(allergy.Name));
        return [...availableFromDB.map(a => a.Name), 'Other'];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        // Validation
        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            setError('First Name and Last Name are required');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            setError('Please enter a valid email address');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        try {
            setLoading(true);

            const updateData = {
                id: member.ID,
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                gender: formData.gender || null,
                dateOfBirth: formData.dateOfBirth || null,
                phone: formData.phone || null,
                email: formData.email || null,
                address1: formData.address1 || null,
                address2: formData.address2 || null,
                city: formData.city || null,
                province: formData.province || null,
                country: formData.country || null,
                postalCode: formData.postalCode || null,
                familySize: formData.familySize ? parseInt(formData.familySize) : null,
                allergy: formData.allergy || null,
                notificationConsent: formData.notificationConsent,
                notes: formData.notes || null,
                isActive: formData.isActive,
            };

            await memberService.updateMember(updateData);

            // Upsert picture only if user retook the picture
            if (capturedPicture) {
                try {
                    await memberService.upsertMemberPicture(member.ID, capturedPicture);
                } catch (picErr) {
                    console.error('Error saving member picture:', picErr);
                }
            }

            setSuccess(true);

            // Redirect back to member detail after 2 seconds
            setTimeout(() => {
                console.log('Redirecting with memberId:', member.ID);
                navigate(`/member-detail?id=${member.ID}`);
            }, 2000);
        } catch (err) {
            console.error('Error updating member:', err);
            setError(err.response?.data?.error || 'Failed to update member. Please try again.');
            // Scroll to top to show the error message
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (!member) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">No member data provided</Alert>
                <Button variant="outlined" onClick={() => navigate('/members')} sx={{ mt: 2 }}>
                    Back to Member List
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{ mr: 2 }}
                >
                    <ArrowBack />
                </Button>
                <Typography variant="h4" component="h1" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Edit Member
                </Typography>
            </Box>

            {/* Success Message */}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Member updated successfully! Redirecting...
                </Alert>
            )}

            {/* Error Message */}
            {error && (
                <Alert 
                    severity="error" 
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => setError(null)}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{ 
                        mb: 3,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                >
                    {error}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                {/* Member Information */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Member Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Membership Number</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>{member.MembershipNumber || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Member Since</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                {new Date(member.CreatedDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Basic Information */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Basic Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Row 1: First Name, Last Name */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="First Name"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                    
                    {/* Row 2: Phone, Email */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="email"
                                label="Email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                    
                    {/* Row 3: Gender, DOB */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleChange}
                                    label="Gender"
                                >
                                    <MenuItem value="">Select...</MenuItem>
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                    <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date of Birth"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    {/* Member Picture */}
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Photo:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: '2px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'grey.200',
                                }}
                            >
                                <img
                                    src={capturedPicture || pictureUrl || DEFAULT_AVATAR}
                                    alt="Member"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = DEFAULT_AVATAR;
                                    }}
                                />
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<CameraAlt />}
                                onClick={() => setCameraOpen(true)}
                            >
                                Take Photo
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Address */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Address
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Row 1: Address 1 */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address 1"
                                name="address1"
                                value={formData.address1}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                    
                    {/* Row 2: Address 2 */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address 2"
                                name="address2"
                                value={formData.address2}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                    
                    {/* Row 3: City, Province */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Province"
                                name="province"
                                value={formData.province}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                    
                    {/* Row 4: Country, Postal Code */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Postal Code"
                                name="postalCode"
                                value={formData.postalCode}
                                onChange={handleChange}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Allergies and Notes */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Additional Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {/* Allergies Section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Allergies
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedAllergies.map((allergy, index) => (
                                <Chip
                                    key={index}
                                    label={allergy}
                                    onDelete={() => handleRemoveAllergy(allergy)}
                                    deleteIcon={<CloseIcon />}
                                    variant="outlined"
                                    color="primary"
                                />
                            ))}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setAddAllergyDialogOpen(true)}
                                sx={{ height: 32 }}
                            >
                                Add Allergy
                            </Button>
                        </Box>
                    </Box>
                    
                    {/* Family Size Section */}
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Family Size"
                            name="familySize"
                            value={formData.familySize}
                            onChange={handleChange}
                            inputProps={{ min: 1 }}
                        />
                    </Box>
                    
                    {/* Notification Consent Section */}
                    <Box sx={{ mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="notificationConsent"
                                    checked={formData.notificationConsent}
                                    onChange={handleChange}
                                />
                            }
                            label="Consent to receive notification"
                        />
                    </Box>
                    
                    {/* Other Notes Section */}
                    <Box>
                        <TextField
                            fullWidth
                            label="Other Notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            multiline
                            rows={4}
                        />
                    </Box>
                </Paper>

                {/* Status */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Status
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                    />
                                }
                                label="Is Active Member"
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </form>

            {/* Add Allergy Dialog */}
            <Dialog
                open={addAllergyDialogOpen}
                onClose={() => setAddAllergyDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Select Allergy</DialogTitle>
                <DialogContent>
                    <List>
                        {getAvailableAllergies().map((allergy, index) => (
                            <ListItem key={index} disablePadding>
                                {allergy === 'Other' ? (
                                    <Box sx={{ width: '100%', p: 2 }}>
                                        <Typography variant="body1" sx={{ mb: 1 }}>
                                            Other
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Specify other allergy"
                                            value={otherAllergyText}
                                            onChange={(e) => setOtherAllergyText(e.target.value)}
                                            sx={{ mb: 1 }}
                                        />
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleAddAllergy(allergy)}
                                            disabled={!otherAllergyText.trim()}
                                        >
                                            Add Other Allergy
                                        </Button>
                                    </Box>
                                ) : (
                                    <ListItemButton
                                        onClick={() => handleAddAllergy(allergy)}
                                    >
                                        <ListItemText primary={allergy} />
                                    </ListItemButton>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddAllergyDialogOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Camera for retaking picture */}
            <MemberPictureCamera
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={(dataUrl) => setCapturedPicture(dataUrl)}
            />
        </Box>
    );
};

export default MemberEdit;

